import {
  useState, useCallback, useRef, createContext, useContext, useEffect,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle, AlertTriangle, Info, XCircle, X,
} from 'lucide-react';

const ToastContext = createContext(null);

let toastId = 0;

const ICON_BY_TYPE = {
  success: CheckCircle,
  warning: AlertTriangle,
  info: Info,
  error: XCircle,
};

function ToastIcon({ type }) {
  const Icon = ICON_BY_TYPE[type] || ICON_BY_TYPE.info;
  return <Icon size={18} strokeWidth={1.5} aria-hidden="true" />;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const clearTimer = useCallback((id) => {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  const scheduleDismiss = useCallback((id, duration) => {
    clearTimer(id);
    timersRef.current[id] = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      delete timersRef.current[id];
    }, duration);
  }, [clearTimer]);

  const addToast = useCallback((
    message,
    type = 'success',
    duration = type === 'warning' ? 5000 : type === 'error' ? 6000 : 3000,
  ) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message, type, duration }]);
    scheduleDismiss(id, duration);
    return id;
  }, [scheduleDismiss]);

  const removeToast = useCallback((id) => {
    clearTimer(id);
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, [clearTimer]);

  const handleMouseEnter = useCallback((id) => {
    clearTimer(id);
  }, [clearTimer]);

  const handleMouseLeave = useCallback((id, duration) => {
    const remaining = duration * 0.4;
    scheduleDismiss(id, remaining);
  }, [scheduleDismiss]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  return (
    <ToastContext.Provider value={addToast}>
      {children}
      <div className="toast-container" role="region" aria-label="Notifications">
        <AnimatePresence mode="popLayout">
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              layout
              initial={{ opacity: 0, y: 24, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 28 }}
              className={`toast toast--${toast.type}`}
              onClick={() => removeToast(toast.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') removeToast(toast.id); }}
              onMouseEnter={() => handleMouseEnter(toast.id)}
              onMouseLeave={() => handleMouseLeave(toast.id, toast.duration)}
              role="alert"
              aria-live="polite"
              tabIndex={0}
            >
              <span className="toast__icon">
                <ToastIcon type={toast.type} />
              </span>
              <span className="toast__message">{toast.message}</span>
              <button
                type="button"
                className="toast__close"
                onClick={(e) => { e.stopPropagation(); removeToast(toast.id); }}
                aria-label="Dismiss notification"
                tabIndex={0}
              >
                <X size={14} strokeWidth={1.5} />
              </button>
              <motion.div
                className="toast__progress"
                initial={{ scaleX: 1 }}
                animate={{ scaleX: 0 }}
                transition={{ duration: toast.duration / 1000, ease: 'linear' }}
                style={{ originX: 0 }}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
